import OpenAI from "openai"
import { RecursiveCharacterTextSplitter, } from '@langchain/textsplitters'
import { estimateTokenCount } from 'tokenx'
import { documents } from "@/lib/agents/search/documents"

declare global {
  interface ExtractedDocument {
    locale: string
    text: string
    chunks?: string[]
  }
}


export function extractLocaleAndText(fullText: string): ExtractedDocument {
  let locale = ""
  let text = fullText || ""
  if (text.startsWith("**Detected locale:**")) {
    const firstLineEnd = text.indexOf("\n")
    const localeLine = text.substring(0, firstLineEnd).trim()
    const localeMatch = localeLine.match(/\*\*Detected locale:\*\* (\S+)/)
    if (localeMatch) {
      locale = localeMatch[1]
      text = text.substring(firstLineEnd).trim()
    }
  }
  locale = locale || "en-US"
  return { locale, text }
}

export async function uploadFile(file: File, client: OpenAI, purpose: OpenAI.Files.FilePurpose) {
  const fileu = await client.files.create({
    file: file,
    purpose,
    expires_after: {
      anchor: "created_at",
      seconds: 60 * 60
    }
  })
  return fileu
}

export async function chunkText(text: string, isMarkDown = true, chunkSize = 1024): Promise<string[]> {
  if (!text) return []
  if (text.length <= chunkSize) return []
  if (isMarkDown) {
    const mdSplitter = RecursiveCharacterTextSplitter.fromLanguage("markdown", { chunkSize, chunkOverlap: 0 })
    return await mdSplitter.splitText(text)
  } else {
    const textSplitter = new RecursiveCharacterTextSplitter({ chunkSize, chunkOverlap: 0, })
    return await textSplitter.splitText(text)
  }

}

export async function convertTextToMarkdown({ buffer, extension, chunkSize = 1024 }: { buffer: ArrayBuffer, extension: SupportedVectorExtensions, chunkSize?: number }): Promise<ExtractedDocument> {

  const text = new TextDecoder('utf-8').decode(buffer)

  let r: ExtractedDocument = {
    text,
    locale: "en-US",
    chunks: []
  }

  if (!text || extension == "json" || extension == "xml") return r

  r.chunks = await chunkText(text, false, chunkSize)

  return r

}


interface Embeddings {
  embedding: number[]
  tokens: number
  content?: string
}

// tokenx's estimateTokenCount is a heuristic and can undercount badly (e.g. any digit run counts as a
// single "token" no matter how long), so a pre-emptive size check isn't reliable. Instead, only split when
// the embeddings call actually reports the input is too long, and split just that piece.
function isContextLengthError(error: any): boolean {
  const message = error?.message ?? ""
  return error?.status === 400 && /maximum context length|context_length_exceeded/i.test(message)
}

function splitInHalf(text: string): [string, string] {
  const mid = Math.floor(text.length / 2)
  const spaceBeforeMid = text.lastIndexOf(" ", mid)
  const cut = spaceBeforeMid > 0 ? spaceBeforeMid + 1 : mid
  return [text.slice(0, cut), text.slice(cut)]
}

async function embedRecursive(input: string, apiKey: string): Promise<Embeddings[]> {
  try {
    const embedding = await documents.createEmbedding(input, apiKey)
    return [{ embedding, tokens: estimateTokenCount(input), content: input }]
  } catch (error) {
    if (!isContextLengthError(error) || input.length < 2) throw error
    const [first, second] = splitInHalf(input)
    const [firstResult, secondResult] = await Promise.all([
      embedRecursive(first, apiKey),
      embedRecursive(second, apiKey),
    ])
    return [...firstResult, ...secondResult]
  }
}

export async function createEmbeddings(input: string, apiKey: string): Promise<Embeddings[]> {
  if (!input) return []
  return embedRecursive(input, apiKey)
}
